import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Upload, ImageOff, CheckCircle2 } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

function MissingRow({ path, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const res = await fetch(`${API}/admin/media/wp/${path}`, { method: "POST", headers: authHeader(), body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Upload failed");
      toast.success("Image replaced"); onDone();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <li className="flex items-center gap-3 py-3 border-b border-sand-dark last:border-0" data-testid={`media-missing-${path}`}>
      <ImageOff className="w-4 h-4 text-terracotta shrink-0" />
      <span className="flex-1 font-mono text-xs text-ink-secondary break-all">{path}</span>
      <label className="h-9 px-3 rounded-sm bg-moss text-white text-sm font-semibold flex items-center gap-1.5 cursor-pointer shrink-0">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Replace
        <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} data-testid={`media-upload-${path}`} />
      </label>
    </li>
  );
}

export default function MediaRecoveryAdmin() {
  const [s, setS] = useState(null);
  const load = useCallback(() => fetch(`${API}/admin/media/wp-status`, { headers: authHeader() }).then((r) => r.json()).then(setS).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!s?.syncing) return; const id = setInterval(load, 8000); return () => clearInterval(id); }, [s?.syncing, load]);

  const sync = async () => {
    await fetch(`${API}/admin/media/wp-sync`, { method: "POST", headers: authHeader() });
    toast.success("Retrying missing images from the Internet Archive…"); setTimeout(load, 1500);
  };

  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-moss" />;
  return (
    <div className="max-w-3xl space-y-6" data-testid="media-recovery-admin">
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight">Site images</h2>
        <p className="text-sm text-ink-secondary mt-1">Photos from the old WordPress site, recovered from the Internet Archive and now stored on this site. Anything not found shows a placeholder until you replace it.</p>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <span className="inline-flex items-center gap-2 text-sm" data-testid="media-ok-count"><CheckCircle2 className="w-4 h-4 text-moss" /> {s.ok} recovered</span>
        <span className="inline-flex items-center gap-2 text-sm" data-testid="media-missing-count"><ImageOff className="w-4 h-4 text-terracotta" /> {s.missing.length} missing</span>
        <button onClick={sync} disabled={s.syncing} data-testid="media-sync-btn"
          className="h-9 px-3 rounded-sm border border-sand-dark text-sm font-medium flex items-center gap-1.5 hover:bg-sand disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${s.syncing ? "animate-spin" : ""}`} /> {s.syncing ? "Recovering…" : "Retry missing"}
        </button>
      </div>
      {s.missing.length > 0 && (
        <ul className="bg-white border border-sand-dark rounded-sm px-4" data-testid="media-missing-list">
          {s.missing.map((p) => <MissingRow key={p} path={p} onDone={load} />)}
        </ul>
      )}
    </div>
  );
}
